import CertificateElementable from "../certificateElementable";
import Validatable from "~/mixins/validatable";
import CertificateBuilderModel from "~/models/tracked/certificateBuilderModel";
import {certificateElementType} from "~/enums/certificateElementType";
import Certificate from "./certificate";
import CertificateElement from "./certificateElement";
import CertificateElementHandler from "~/services/certificateElementHandler";
import IRepo from "~/interfaces/repo";
import Repo from "~/state/modules/repo";
import {certificateElementCapability} from "./certificateElementCapability";
import CertificateElementCapabilityManager from "./certificateElementCapabilityManager";
import {RelationType} from "~/models/relationType";
import DynamicModeCertificateElement from "./dynamicCertificateElement";
import PageCertificateElement from "./pageCertificateElement";
import GridCertificateElement from "./gridCertificateElement";
import Guid from "~/helpers/guid";
import CertificateElementValidation from "~/models/navigableValidations/CertificateElementValidation";
import PageCertificateElementValidation from "~/models/navigableValidations/pageCertificateElementValidator";
import CertificateElementDefinition from "~/models/certificates/certificateElementDefinition";
import {HorizontalAlignment, VerticalAlignment} from "~/enums/certificates/alignment";
import AdaptiveLayoutSection from "./adaptiveLayoutSection";
import CertificateFoundationLayer from "./certificateFoundationLayer";
import CertificateFoundationLayerFactory from "./certificateFoundationLayerFactory";
import {Hash} from "~/interfaces/hashInterfaces";
import {ObjectHelper} from "~/helpers/objectHelper";

export default class BaseCertificateElement extends CertificateElementable(Validatable(CertificateBuilderModel)) {
  id: string;
  htmlNode: string;
  width: number;
  height: number;
  type: certificateElementType;
  rotation: number;
  c_opacity: number;
  sort_order: number;
  scale_x: number;
  scale_y: number;
  parent:  PageCertificateElement | DynamicModeCertificateElement | GridCertificateElement | Certificate | AdaptiveLayoutSection
  except =  [ 'createEmbeddedObject', 'organization_id', 'htmlImage', 'dynamic_input_name', '$element'];
  certificate_foundation_layers: Hash<CertificateFoundationLayer> = {};
  foundation_layer_dependents: { id: string, title: string }[];

  _certificateElementHandler = new CertificateElementHandler(this, CertificateElement, 'certificate_elements');

  static repo: IRepo<BaseCertificateElement> = new Repo<BaseCertificateElement>('certificateElements', BaseCertificateElement);

  get certificate(): Certificate {
    if (this.parent instanceof Certificate) {
      return this.parent;
    } else {
      return this._rootModel;
    }
  }


  // ********** Selecting ***************
  get selector() {
    return this.certificate.selector;
  }
  get selectedCertificateElement() {
    return this.selector.get();
  }

  get isSelected(): boolean{
    return this.selector?.get()?.client_id === this.client_id
  }

  get getCertificateElements(): CertificateElement[] {
    const elements = []
    this.certificateElementsArray.forEach(element =>{
      elements.push(element);
      elements.push(...element.getCertificateElements);
    })
    return elements
  }

  get selfOrDescendentIsSelected(): boolean {
    if (this.isSelected) {
      return true
    }
    return this.certificateElementsArrayDeep.some(ce => ce.isSelected)
  }

  select(): void {
    this.selector.set(this);
  }

  // ********** Abilities ***************


  can(duty: certificateElementCapability): boolean {
    return CertificateElementCapabilityManager.can(this.type, duty);
  }
  get isWithinVueDraggable(): boolean {
    return this.isDraggable || this.isResizeable;
  }

  get isDraggable(){
    return this.can(certificateElementCapability.draggable)
  }

  get isResizeable(){
    return this.can(certificateElementCapability.resizeable)
  }

  get rootName(): string {
    return 'certificate_elements';
  }

  get certificateId(): string {
    //This is a temporary fix - Return unpublished certificate id for published certificates
    return this.certificate.isPublishedCertificate ?  this.certificate.certificate_id : this.certificate.id;
  }

  createEmbeddedObject = {
    certificate_elements: (parent, json): CertificateElement => {
      return Certificate.certificateElementFactory(json["type"])
    },
    //Used for adaptive layout sections
    certificate_foundation_layers: (parent, json): CertificateFoundationLayer => {
      return CertificateFoundationLayerFactory.create(this, json);
    },

  };

  get EMBEDDED_MODELS(): { [p: string]: { type: any; relationType: RelationType } } {
    return {
      'certificate_elements': {type: CertificateElement, relationType: RelationType.dictionary},
      'certificate_foundation_layers': {type: CertificateFoundationLayer, relationType: RelationType.dictionary},
    };
  }
  constructor() {
    super();
    this.defineProperty('client_id', Guid.create());
  }


  initialize(root, parent = root, type, json= {}) {
    this.rootModel = root;
    this.parent = parent;
    this.defineProperty('sort_order', json["sort_order"]  || 0);
    this.defineProperty('type', type);
    CertificateElementCapabilityManager.addDefaultProperties(this, json)
    this.loadFromJson(json);
    return this;
  }

  //******************** VALIDATABLE ********************//
  _validation: CertificateElementValidation | PageCertificateElementValidation = new CertificateElementValidation(this);

  addToRepo(): void {
    CertificateElement.repo.add(this)
  }

  get displayName(): string {
    return CertificateElementDefinition.getDisplayName(this.type);
  }
  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case "underlying_variable":
      case "uploadImageFormElementId":
        return "Dynamic Image"
        break;
      default:
        return propName;
    }
  }

  get foundationLayers() {
    return ObjectHelper.toArray(this.certificate_foundation_layers);
  }

  get foundationLayerElements(): CertificateElement[] {
    const elements = [];
    for (const layer of this.foundationLayers) {
        const sourceDoc = this.certificate.foundationLayerSourceDocuments.find(doc => (doc.certificate_id || doc.id) === layer.source_document);
        if (!sourceDoc) continue;
        const sourcePage = sourceDoc.pageElements.find(page => page.client_id === layer.source_page);
        elements.push(...sourcePage.certificateElementsArray);
    }
    return elements;
  }

  get foundationLayerSourceDocuments(): Certificate[] {
    return this.foundationLayers
      .map(layer => Certificate.repo.findWhere(certificate => (certificate.certificate_id || certificate.id) === layer.source_document)[0])
      .filter(sourceDoc => !!sourceDoc && !!sourceDoc.id);
  }
  get hasFoundationLayerDependents(): boolean {
    return this.foundation_layer_dependents.length > 0;
  }


}
